<template>
	<div v-if="updated" class="pwa-toast" role="status">
		<div class="mb-2 text-black">
			{{ translate('component.updateDialog.updated') }}
		</div>
		<div class="d-flex ga-2">
			<ButtonIcon
				type="releaseNote"
				href="https://api.hausperfekt.de/patch-notes?projectKey=AAR"
				target="_blank"
				rel="noopener"
			/>
			<ButtonIcon type="close" @click="updated = false" />
		</div>
	</div>
</template>

<script setup lang="ts">
//https://vite-pwa-org.netlify.app/frameworks/vue.html
// registerType 'autoUpdate': the new SW skips waiting + claims clients on its own;
// the plugin reloads the page once on 'activated'. We only leave a flag so the
// reloaded page can tell the user what happened (information, not a trigger).
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { ref, onUnmounted } from 'vue'
import { useTranslate } from 'composables/useTranslate.ts'

const intervalMS = 30 * 60 * 1000
const FLAG = 'pwa-updated'

let swUpdateInterval: ReturnType<typeof setInterval> | null = null
const { translate } = useTranslate()

const readFlag = (): boolean => {
	try {
		const set = sessionStorage.getItem(FLAG) === '1'
		sessionStorage.removeItem(FLAG)
		return set
	} catch {
		return false
	}
}
const updated = ref(readFlag())

useRegisterSW({
	immediate: true,
	onNeedReload() {
		try {
			sessionStorage.setItem(FLAG, '1')
		} catch {
			// storage unavailable: reload without the info toast
		}
		window.location.reload()
	},
	onRegisterError(error) {
		console.error('Service Worker registration failed:', error)
	},
	onRegisteredSW(swUrl, r) {
		if (r) {
			swUpdateInterval = setInterval(async () => {
				try {
					if (!r || r.installing || r.waiting || !navigator?.onLine) return

					const resp = await fetch(swUrl, {
						cache: 'no-store',
						headers: {
							'cache-control': 'no-cache',
						},
					})

					if (resp?.status === 200) {
						await r.update()
					}
				} catch (err) {
					console.error('SW update check failed:', err)
				}
			}, intervalMS)
		}
	},
})

onUnmounted(() => {
	if (swUpdateInterval) {
		clearInterval(swUpdateInterval)
	}
})
</script>

<style scoped>
.pwa-toast {
	position: fixed;
	right: 0;
	bottom: 0;
	margin: 16px;
	padding: 12px;
	border: 1px solid #8885;
	border-radius: 4px;
	z-index: 10000;
	text-align: left;
	box-shadow: 3px 4px 5px 0 #8885;
	background-color: white;
}
</style>
